import { useCallback, useRef, useState } from 'react';
import { api, ApiError } from './api';

export type UploadPhase = 'idle' | 'uploading' | 'processing' | 'done' | 'error';

export interface UploadState {
  /** 0-100. Sits at 99 while the server processes the files. */
  percent: number;
  phase: UploadPhase;
  /** Human-readable reason the last attempt failed, or '' when there is none. */
  error: string;
}

const IDLE: UploadState = { percent: 0, phase: 'idle', error: '' };

/**
 * Turns an upload into explicit UI state: a progress percentage, a phase the
 * button can label itself from, and the real server message when it fails.
 *
 * `run` returns the parsed response on success and throws on failure, so a
 * caller can keep its existing try/catch and still get progress + errors.
 */
export function useUpload() {
  const [state, setState] = useState<UploadState>(IDLE);
  const abortRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setState(IDLE);
  }, []);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setState((s) => (s.phase === 'uploading' || s.phase === 'processing'
      ? { percent: s.percent, phase: 'error', error: 'Upload cancelled.' }
      : s));
  }, []);

  const run = useCallback(async <T,>(
    path: string,
    formData: FormData,
    method: 'POST' | 'PUT' = 'POST'
  ): Promise<T> => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setState({ percent: 0, phase: 'uploading', error: '' });

    try {
      const result = await (method === 'PUT' ? api.uploadPut : api.upload)<T>(
        path,
        formData,
        (percent) => {
          setState((s) => {
            if (s.phase === 'error') return s;
            // The browser only reports the request body being sent. Once it
            // reaches ~99 the server is writing files and inserting rows, which
            // is a different (and slower) phase worth showing on its own.
            const phase: UploadPhase = percent >= 100 ? 'processing' : 'uploading';
            return { percent, phase, error: '' };
          });
        }
      );
      setState({ percent: 100, phase: 'done', error: '' });
      return result;
    } catch (err) {
      const e = err as ApiError;
      // A cancel is a deliberate user action, not a failure to report.
      if (e?.name === 'AbortError' || /cancell?ed/i.test(e?.message || '')) {
        setState((s) => ({ ...s, phase: 'error', error: 'Upload cancelled.' }));
      } else {
        setState({
          percent: 0,
          phase: 'error',
          error: e?.message || 'The upload failed for an unknown reason. Please try again.',
        });
      }
      throw err;
    } finally {
      abortRef.current = null;
    }
  }, []);

  return { ...state, busy: state.phase === 'uploading' || state.phase === 'processing', run, reset, cancel };
}